import React, { useState, useEffect } from 'react';
import CommonHeader from './CommonHeader';
import CommonFooter from './CommonFooter';
import config from '../config';
import './LegalPages.css';

const CookiePolicy = ({ onNavigate, onLogin, onSignup }) => {
  const currentYear = new Date().getFullYear();
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false
  });
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    // Load saved preferences from localStorage
    const saved = localStorage.getItem('cookiePreferences');
    if (saved) {
      try {
        const prefs = JSON.parse(saved);
        setCookiePreferences(prefs);
      } catch (e) {
        console.error('Error loading cookie preferences:', e);
      }
    }
  }, []);

  const savePreferences = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    setShowPreferences(false);
    // You can also send preferences to server here
    alert('Cookie preferences saved successfully!');
  };

  const acceptAll = () => {
    const allTrue = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    setCookiePreferences(allTrue);
    localStorage.setItem('cookiePreferences', JSON.stringify(allTrue));
    alert('You have accepted all cookies. Preferences saved.');
  };

  const rejectAll = () => {
    const allFalse = {
      necessary: true, // Necessary cookies cannot be disabled
      functional: false,
      analytics: false,
      marketing: false
    };
    setCookiePreferences(allFalse);
    localStorage.setItem('cookiePreferences', JSON.stringify(allFalse));
    alert('You have rejected non-essential cookies. Preferences saved.');
  };

  return (
    <div className="legal-page">
      <CommonHeader
        activePage="cookies"
        onNavigate={onNavigate}
        onLogin={onLogin}
        onSignup={onSignup}
      />

      <div className="legal-container">
        <div className="legal-content">
          <div className="legal-header">
            <h1>🍪 Cookie Policy</h1>
            <p className="last-updated">Last Updated: January 1, {currentYear}</p>
          </div>

          <div className="legal-body">
            <section>
              <h2>1. What Are Cookies?</h2>
              <p>Cookies are small text files that are placed on your computer, smartphone, or other device when you visit our website. They help us provide you with a better experience and allow certain features to function properly.</p>
              <p>Cookies are widely used by website owners to make their websites work more efficiently, as well as to provide reporting information.</p>
            </section>

            <section>
              <h2>2. How We Use Cookies</h2>
              <p>We use cookies for the following purposes:</p>
              <ul>
                <li><strong>Essential Cookies:</strong> Required for the website to function properly (authentication, security, form submission)</li>
                <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website</li>
                <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
              </ul>
            </section>

            <section>
              <h2>3. Types of Cookies We Use</h2>
              
              <div className="cookie-table">
                <table>
                  <thead>
                    <tr>
                      <th>Cookie Type</th>
                      <th>Purpose</th>
                      <th>Duration</th>
                      <th>Required?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>sessionid</td>
                      <td>Maintains your login session</td>
                      <td>Session</td>
                      <td>Yes</td>
                    </tr>
                    <tr>
                      <td>csrftoken</td>
                      <td>Protects against cross-site request forgery</td>
                      <td>1 year</td>
                      <td>Yes</td>
                    </tr>
                    <tr>
                      <td>preferences</td>
                      <td>Stores your user preferences</td>
                      <td>1 year</td>
                      <td>No</td>
                    </tr>
                    <tr>
                      <td>_ga</td>
                      <td>Google Analytics - distinguishes users</td>
                      <td>2 years</td>
                      <td>No</td>
                    </tr>
                    <tr>
                      <td>_gid</td>
                      <td>Google Analytics - distinguishes users</td>
                      <td>24 hours</td>
                      <td>No</td>
                    </tr>
                    <tr>
                      <td>_fbp</td>
                      <td>Facebook Pixel - advertising</td>
                      <td>3 months</td>
                      <td>No</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2>4. Third-Party Cookies</h2>
              <p>We use the following third-party services that may place cookies on your device:</p>
              <ul>
                <li><strong>Google Analytics:</strong> To analyze website traffic and user behavior</li>
                <li><strong>Stripe:</strong> For payment processing (if applicable)</li>
                <li><strong>AWS:</strong> For image storage and processing</li>
              </ul>
              <p>These third parties have their own privacy policies, and we encourage you to review them.</p>
            </section>

            <section>
              <h2>5. Your Cookie Preferences</h2>
              <p>You can control and manage your cookie preferences in several ways:</p>
              <ul>
                <li>Use the cookie preference manager below</li>
                <li>Adjust your browser settings to block or delete cookies</li>
                <li>Opt-out of Google Analytics using the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics opt-out browser add-on</a></li>
              </ul>
              
              <div className="cookie-preferences">
                <div className="preferences-header">
                  <h3>Cookie Preference Center</h3>
                  <button 
                    className="preferences-toggle"
                    onClick={() => setShowPreferences(!showPreferences)}
                  >
                    {showPreferences ? 'Hide Preferences' : 'Customize Settings'}
                  </button>
                </div>

                {showPreferences && (
                  <div className="preferences-panel">
                    <div className="preference-item">
                      <div className="preference-info">
                        <strong>Necessary Cookies</strong>
                        <p>Required for the website to function. Cannot be disabled.</p>
                      </div>
                      <div className="preference-status">
                        <span className="always-on">Always On</span>
                      </div>
                    </div>

                    <div className="preference-item">
                      <div className="preference-info">
                        <strong>Functional Cookies</strong>
                        <p>Remember your preferences and settings for a better experience.</p>
                      </div>
                      <label className="switch">
                        <input 
                          type="checkbox" 
                          checked={cookiePreferences.functional}
                          onChange={(e) => setCookiePreferences({...cookiePreferences, functional: e.target.checked})}
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>

                    <div className="preference-item">
                      <div className="preference-info">
                        <strong>Analytics Cookies</strong>
                        <p>Help us understand how visitors use our website to improve it.</p>
                      </div>
                      <label className="switch">
                        <input 
                          type="checkbox" 
                          checked={cookiePreferences.analytics}
                          onChange={(e) => setCookiePreferences({...cookiePreferences, analytics: e.target.checked})}
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>

                    <div className="preference-item">
                      <div className="preference-info">
                        <strong>Marketing Cookies</strong>
                        <p>Used to deliver relevant advertisements to you.</p>
                      </div>
                      <label className="switch">
                        <input 
                          type="checkbox" 
                          checked={cookiePreferences.marketing}
                          onChange={(e) => setCookiePreferences({...cookiePreferences, marketing: e.target.checked})}
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>

                    <div className="preferences-actions">
                      <button onClick={savePreferences} className="save-preferences-btn">
                        Save Preferences
                      </button>
                    </div>
                  </div>
                )}

                <div className="quick-actions">
                  <button onClick={acceptAll} className="accept-all-btn">
                    Accept All Cookies
                  </button>
                  <button onClick={rejectAll} className="reject-all-btn">
                    Reject Non-Essential
                  </button>
                </div>
              </div>
            </section>

            <section>
              <h2>6. Managing Cookies in Your Browser</h2>
              <p>Most web browsers allow you to control cookies through their settings. You can:</p>
              <ul>
                <li>See what cookies are stored on your device</li>
                <li>Delete all cookies</li>
                <li>Block cookies from specific websites</li>
                <li>Block all cookies from being set</li>
              </ul>
              <p>Here are links to cookie management instructions for popular browsers:</p>
              <ul>
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-1b95-0a9ac8f2c2c4" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
              </ul>
            </section>

            <section>
              <h2>7. Updates to This Cookie Policy</h2>
              <p>We may update this Cookie Policy from time to time to reflect changes in technology, legal requirements, or our practices. Any changes will be posted on this page with an updated "Last Updated" date.</p>
            </section>

            <section>
              <h2>8. Contact Us</h2>
              <p>If you have any questions about our use of cookies, please contact us:</p>
              <ul>
                <li>Email: <a href={`mailto:${config.PRIVACY_EMAIL}`}>{config.PRIVACY_EMAIL}</a></li>
                <li>Support: <a href={`mailto:${config.SUPPORT_EMAIL}`}>{config.SUPPORT_EMAIL}</a></li>
              </ul>
            </section>
          </div>
        </div>
      </div>

      <CommonFooter onNavigate={onNavigate} activePage="cookies" />
    </div>
  );
};

export default CookiePolicy;